import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { SelectButton } from 'primereact/selectbutton';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../components/ui/Button';
import { Tag } from 'primereact/tag';

import { useCustomToast } from '../Contexts/ToastContext';
import { useGetAutomations, useUpdateAutomation, useSendTest } from '../../queries/marketing';

const META = {
  birthday: { icon: '🎂', color: 'warning' },
  winback:  { icon: '💤', color: 'info' },
  spin:     { icon: '🎁', color: 'success' },
};

/**
 * Marketing → Automations. The 3 always-on auto campaigns (birthday 1-day-before,
 * win-back, spin follow-up), each a card with an on/off switch + edit-content
 * dialog + test send. Seeded OFF — the venue reviews copy before activating.
 */
export default function AutomationsPanel() {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  const { data, isLoading } = useGetAutomations();
  const update = useUpdateAutomation();
  const test = useSendTest();

  const [editing, setEditing] = useState(null); // the automation being edited
  const [form, setForm] = useState(null);

  const rows = data?.automations ?? [];

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      template_subject: a.template_subject || '',
      template_body: a.template_body || '',
      direction: a.direction || 'auto',
      cta_label: a.cta_label || '',
      cta_url: a.cta_url || '',
      inactive_days: a.target_filter?.inactive_days ?? 14,
    });
  };

  const toggle = (a, value) =>
    update.mutate({ id: a.id, is_active: value });

  const saveEdit = async () => {
    const payload = {
      id: editing.id,
      template_subject: form.template_subject.trim(),
      template_body: form.template_body.trim(),
      direction: form.direction,
      cta_label: form.cta_label.trim() || null,
      cta_url: form.cta_url.trim() || null,
    };
    if (editing.trigger_type === 'inactive_14days') payload.inactive_days = form.inactive_days;
    await update.mutateAsync(payload);
    setEditing(null);
  };

  const sendTest = async (a) => {
    const res = await test.mutateAsync(a.id);
    if (res?.sent) showToast('success', 'Marketing', t('marketing.test_sent', { to: res.to }));
    else showToast('error', 'Marketing', t('marketing.test_failed'));
  };

  const directionOptions = [
    { label: t('marketing.dir_auto'), value: 'auto' },
    { label: t('marketing.dir_rtl'), value: 'rtl' },
    { label: t('marketing.dir_ltr'), value: 'ltr' },
  ];

  if (isLoading) return <div className="p-4 text-500">{t('marketing.loading')}</div>;

  return (
    <div className="grid">
      {rows.map((a) => {
        const meta = META[a.key] ?? { icon: '📣', color: null };
        return (
          <div key={a.id} className="col-12 md:col-4">
            <div className="surface-card border-round p-3 h-full flex flex-column gap-2">
              <div className="flex align-items-center justify-content-between">
                <span style={{ fontSize: '1.6rem' }}>{meta.icon}</span>
                <InputSwitch checked={!!a.is_active} onChange={(e) => toggle(a, e.value)} />
              </div>
              <div className="font-bold text-lg">{t(`marketing.auto_${a.key}_title`)}</div>
              <div className="text-500 text-sm flex-grow-1">{t(`marketing.auto_${a.key}_desc`)}</div>
              <div>
                <Tag severity={a.is_active ? meta.color : 'secondary'}
                  value={a.is_active ? t('marketing.auto_on') : t('marketing.auto_off')} />
              </div>
              <div className="flex gap-2 mt-1">
                <Button size="small" outlined icon="pi pi-pencil" label={t('marketing.auto_edit')} onClick={() => openEdit(a)} />
                <Button size="small" text icon="pi pi-send" label={t('marketing.send_test')}
                  loading={test.isLoading} onClick={() => sendTest(a)} />
              </div>
            </div>
          </div>
        );
      })}

      <Dialog header={editing ? t(`marketing.auto_${editing.key}_title`) : ''} visible={!!editing}
        style={{ width: '42rem', maxWidth: '95vw' }} onHide={() => setEditing(null)} dismissableMask>
        {form && (
          <div className="flex flex-column gap-3 pt-2">
            <div>
              <label className="block mb-1 font-medium">{t('marketing.field_subject')}</label>
              <InputText value={form.template_subject} onChange={(e) => setForm({ ...form, template_subject: e.target.value })} className="w-full" />
            </div>
            <div>
              <label className="block mb-1 font-medium">{t('marketing.field_body')}</label>
              <InputTextarea value={form.template_body} rows={6} autoResize className="w-full"
                dir={form.direction === 'rtl' ? 'rtl' : form.direction === 'ltr' ? 'ltr' : 'auto'}
                onChange={(e) => setForm({ ...form, template_body: e.target.value })} />
              <small className="text-500">{t('marketing.body_hint')}</small>
            </div>
            <div>
              <label className="block mb-1 font-medium">{t('marketing.field_direction')}</label>
              <SelectButton value={form.direction} options={directionOptions}
                onChange={(e) => e.value && setForm({ ...form, direction: e.value })} />
            </div>
            {editing?.trigger_type === 'inactive_14days' && (
              <div>
                <label className="block mb-1 font-medium">{t('marketing.aud_inactive')}</label>
                <div className="flex align-items-center gap-2">
                  <InputNumber value={form.inactive_days} min={1} max={3650} showButtons style={{ width: '9rem' }}
                    onValueChange={(e) => setForm({ ...form, inactive_days: e.value })} />
                  <span className="text-500">{t('marketing.aud_inactive_unit')}</span>
                </div>
              </div>
            )}
            <div className="grid">
              <div className="col-12 md:col-6">
                <label className="block mb-1 font-medium">{t('marketing.field_cta_label')}</label>
                <InputText value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} className="w-full" />
              </div>
              <div className="col-12 md:col-6">
                <label className="block mb-1 font-medium">{t('marketing.field_cta_url')}</label>
                <InputText value={form.cta_url} dir="ltr" onChange={(e) => setForm({ ...form, cta_url: e.target.value })} className="w-full"
                  placeholder="https://wa.me/9665… / https://…" />
              </div>
            </div>
            <div className="flex justify-content-end gap-2">
              <Button outlined severity="secondary" label={t('common.back')} onClick={() => setEditing(null)} />
              <Button label={t('marketing.auto_save')} loading={update.isLoading} onClick={saveEdit}
                disabled={!form.template_subject.trim() || !form.template_body.trim()} />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
